<template>
    <!-- Playground for TreeSelect and the point wrappers. Not a pass/fail page:
         a place to build against and for UI/UX to try configurations. Every
         panel shows its live v-model value. -->
    <div class="flex w-full flex-col gap-6 p-4">
        <section class="rounded-md border border-gray-300 p-4">
            <h2 class="mb-2 text-lg font-semibold">Pick a site</h2>
            <p class="mb-3 text-sm text-gray-600">
                Loads real data for any site you can access. A site with child sites loads as multi-site
                (itself plus every descendant that has a site); anything else loads as a single site. Child
                sites you cannot access show as failed rows. Every site loads through sonicu, so loading
                a parent leaves your session's current site where it was.
            </p>
            <div class="mb-3 w-full sm:w-1/2">
                <select2    :options="pickOptions"
                            v-model:value="picked"
                            placeholder="Select a site"
                            :close-on-select="true"
                ></select2>
            </div>
            <div v-if="pickError" class="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-800">{{ pickError }}</div>
            <template v-else-if="pickInfo">
                <div class="mb-3 rounded bg-gray-100 px-3 py-2 font-mono text-sm">
                    {{ pickMode === 'multi' ? `Parent of ${pickInfo.sites.length} sites, loaded as multi-site` : (pickMode === 'single' ? 'Single site' : 'No site of its own and no child sites') }}
                    <span v-if="pickLoadMs !== null"> | loaded in {{ pickLoadMs.toLocaleString() }} ms</span>
                </div>
                <point-select-tree  v-if="pickMode === 'single'"
                                    :key="`single-${picked}`"
                                    v-model="values.pick"
                                    :company-id="pickInfo.sites[0].id"
                                    @loaded="pickLoaded"
                ></point-select-tree>
                <multi-site-point-select-tree   v-else-if="pickMode === 'multi'"
                                                :key="`multi-${picked}`"
                                                v-model="values.pickMulti"
                                                :sites="pickInfo.sites"
                                                @loaded="pickLoaded"
                ></multi-site-point-select-tree>
                <pre class="mt-2 max-h-32 overflow-auto rounded bg-gray-50 p-2 text-xs">{{ preview(pickMode === 'multi' ? values.pickMulti : values.pick) }}</pre>
            </template>
        </section>

        <section class="rounded-md border border-gray-300 p-4">
            <h2 class="mb-2 text-lg font-semibold">Stress test</h2>
            <p class="mb-3 text-sm text-gray-600">
                Builds a synthetic multi-site tree in the browser (no database). Meter ids restart at 1 in
                every site, so ids repeat across sites on purpose. Search runs with no debounce unless the
                checkbox is on, so by default the timing is the filter itself. Target: under 100 ms for every action.
            </p>
            <div class="mb-3 flex flex-wrap items-end gap-3 text-sm">
                <label class="flex flex-col">Sites<input v-model.number="generator.sites" type="number" min="1" class="w-24 rounded border-gray-300"></label>
                <label class="flex flex-col">Areas per site<input v-model.number="generator.areas" type="number" min="1" class="w-24 rounded border-gray-300"></label>
                <label class="flex flex-col">Zones per area<input v-model.number="generator.zones" type="number" min="1" class="w-24 rounded border-gray-300"></label>
                <label class="flex flex-col">Points per zone<input v-model.number="generator.points" type="number" min="1" class="w-24 rounded border-gray-300"></label>
                <button type="button" class="rounded-md border border-gray-300 bg-white px-3 py-2 font-medium hover:bg-gray-50" @click="buildStressTree">Build</button>
                <span class="text-gray-700">{{ stressLeafCount.toLocaleString() }} points</span>
                <!-- Off: search filters on every keystroke, so the readout times the filter itself.
                     On: the production 150 ms debounce, to judge how typing feels. -->
                <label class="flex items-center gap-2"><input v-model="productionDebounce" type="checkbox" class="rounded border-gray-300">Production search debounce (150 ms)</label>
            </div>
            <div class="mb-3 rounded bg-gray-100 px-3 py-2 font-mono text-sm">
                Last action: {{ lastTiming ? `${lastTiming.action} took ${lastTiming.ms} ms` : 'none yet' }}
                <span v-if="lastTiming && lastTiming.ms >= 100" class="ml-2 font-semibold text-red-700">over target</span>
            </div>
            <div @click.capture="startTiming('click')" @input.capture="startTiming('search')">
                <!-- The key remounts the picker when the debounce changes; TreeSelect sets its debounce up once. -->
                <multi-site-point-select-tree   :key="productionDebounce ? 'debounced' : 'instant'"
                                                v-model="values.stress"
                                                :tree="stressTree"
                                                :search-debounce="productionDebounce ? 150 : 0"
                ></multi-site-point-select-tree>
            </div>
            <pre class="mt-2 max-h-32 overflow-auto rounded bg-gray-50 p-2 text-xs">{{ preview(values.stress) }}</pre>
        </section>

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section v-for="panel in panels" :key="panel.id" class="rounded-md border border-gray-300 p-4">
                <h3 class="mb-1 font-semibold">{{ panel.title }}</h3>
                <p class="mb-3 text-sm text-gray-600">{{ panel.description }}</p>

                <template v-if="panel.real && ! loaded[panel.id]">
                    <button type="button" class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50" @click="loaded[panel.id] = true">Load real data</button>
                </template>
                <template v-else>
                    <tree-select v-if="panel.kind === 'base'" v-model="values[panel.id]" :nodes="panel.nodes"></tree-select>
                    <point-select-tree v-else-if="panel.kind === 'single'" v-model="values[panel.id]" v-bind="panel.props"></point-select-tree>
                    <multi-site-point-select-tree v-else v-model="values[panel.id]" v-bind="panel.props"></multi-site-point-select-tree>
                </template>

                <pre class="mt-2 max-h-32 overflow-auto rounded bg-gray-50 p-2 text-xs">{{ preview(values[panel.id]) }}</pre>
            </section>
        </div>
    </div>
</template>

<script>
import axios from "axios"

// Area > Zone > Point data in the same shape the point-tree endpoint returns.
function syntheticAreas({areas, zones, points, prefix = ''}) {
    let meterId = 1
    return Array.from({length: areas}, (unusedArea, a) => ({
        id: a + 1,
        name: `${prefix}Area ${a + 1}`,
        zones: Array.from({length: zones}, (unusedZone, z) => ({
            id: a * zones + z + 1,
            name: `${prefix}Zone ${a + 1}.${z + 1}`,
            meters: Array.from({length: points}, (unusedPoint, p) => ({
                id: meterId++,
                name: `${prefix}Point ${a + 1}.${z + 1}.${p + 1}`,
            })),
        })),
    }))
}

// Generic nodes for the base component, `depth` levels deep.
function syntheticNodes(depth, breadth, path = 'n') {
    return Array.from({length: breadth}, (unused, i) => {
        const key = `${path}.${i + 1}`
        if (depth === 1) {
            return {key, label: `Leaf ${key.slice(2)}`}
        }

        return {key, label: `Level ${key.slice(2)}`, children: syntheticNodes(depth - 1, breadth, key)}
    })
}

const smallAreas = () => {
    const areas = syntheticAreas({areas: 2, zones: 2, points: 3})
    areas[0].zones[0].meters[0].name = 'A point with a very long name that should be cut with an ellipsis instead of wrapping onto a second line'
    areas[0].zones[0].meters[1].name = null
    return areas
}

export default {
    name: "PointSelectTreeTest",
    props: {
        // [{ id, name }]: the developer's accessible sites, for the real-data panels.
        sites: {
            type: Array,
            default: () => ([]),
        },
        // [{ id, name }]: every active company the developer can access, including enterprise
        // parents with no site of their own, for the "Pick a site" dropdown.
        pickable: {
            type: Array,
            default: () => ([]),
        },
    },
    data() {
        const firstSite = this.sites[0]?.id ?? null

        return {
            generator: {sites: 20, areas: 10, zones: 10, points: 5},
            stressTree: [],
            lastTiming: null,
            productionDebounce: false,
            loaded: {},
            picked: null,
            pickInfo: null,
            pickError: null,
            pickStartedAt: null,
            pickLoadMs: null,
            values: {
                pick: [],
                pickMulti: {},
                stress: {},
                depth2: [],
                depth5: [],
                single: [],
                singleReal: [],
                // 999999 is not in the tree: it must stay selected and counted.
                preselected: [1, 2, 999999],
                expanded: [],
                empty: [],
                emptySite: {},
                duplicates: {},
                multiReal: {},
                multiFailing: {},
            },
            panels: [
                {id: 'depth2', kind: 'base', title: 'TreeSelect, depth 2', description: 'Base component with raw nodes.', nodes: Object.freeze(syntheticNodes(2, 5))},
                {id: 'depth5', kind: 'base', title: 'TreeSelect, depth 5', description: 'Base component, 3 children per node, 243 leaves.', nodes: Object.freeze(syntheticNodes(5, 3))},
                {id: 'single', kind: 'single', title: 'PointSelectTree, small', description: 'One long name and one null name.', props: {tree: smallAreas()}},
                {id: 'singleReal', kind: 'single', real: true, title: 'PointSelectTree, real data', description: `Loads ${firstSite ?? 'no site'} through the API.`, props: {companyId: firstSite}},
                {id: 'preselected', kind: 'single', title: 'PointSelectTree, pre-selected', description: 'Starts with [1, 2, 999999]. 999999 is not in the tree and must stay selected.', props: {tree: smallAreas()}},
                {id: 'expanded', kind: 'single', title: 'PointSelectTree, expanded by default', description: 'expanded-by-default falls through to TreeSelect.', props: {tree: smallAreas(), expandedByDefault: true}},
                {id: 'empty', kind: 'single', title: 'PointSelectTree, empty', description: 'No areas at all.', props: {tree: []}},
                {id: 'emptySite', kind: 'multi', title: 'Multi site, one site with no points', description: 'An empty site next to a normal one.', props: {tree: [{id: 'empty_site', name: 'Empty Site', areas: []}, {id: 'small_site', name: 'Small Site', areas: smallAreas()}]}},
                {id: 'duplicates', kind: 'multi', title: 'Multi site, duplicate names and ids', description: 'Both sites have the same names and meter ids. Selections must stay separate.', props: {tree: [{id: 'site_a', name: 'Site A', areas: syntheticAreas({areas: 2, zones: 2, points: 3})}, {id: 'site_b', name: 'Site B', areas: syntheticAreas({areas: 2, zones: 2, points: 3})}]}},
                {id: 'multiReal', kind: 'multi', real: true, title: 'Multi site, all real sites', description: 'Every site you can access. One request per site, 4 at a time.', props: {sites: this.sites}},
                {id: 'multiFailing', kind: 'multi', real: true, title: 'Multi site, one bad site', description: 'A real site plus not_a_real_site, which must show as an error row.', props: {sites: [this.sites[0], {id: 'not_a_real_site', name: 'not_a_real_site'}].filter(Boolean)}},
            ],
        }
    },
    computed: {
        // sonicu is the parent of every company, so picking it would request every site at once.
        pickOptions() {
            return Object.fromEntries(this.pickable.filter((site) => site.id !== 'sonicu').map((site) => [site.id, site.name]))
        },
        pickMode() {
            if (! this.pickInfo || this.pickInfo.sites.length === 0) {
                return 'none'
            }

            return this.pickInfo.is_parent ? 'multi' : 'single'
        },
        stressLeafCount() {
            return this.stressTree.reduce((total, site) => total + site.areas.reduce(
                (areaTotal, area) => areaTotal + area.zones.reduce((zoneTotal, zone) => zoneTotal + zone.meters.length, 0), 0), 0)
        },
    },
    created() {
        this.buildStressTree()
    },
    watch: {
        picked(companyId) {
            this.pickInfo = null
            this.pickError = null
            this.pickLoadMs = null
            this.values.pick = []
            this.values.pickMulti = {}
            if (! companyId) {
                return
            }

            this.pickStartedAt = performance.now()
            axios.get(`/scripts/ui-tests/point-select-tree/clients/${companyId}`)
                .then((response) => {
                    if (companyId === this.picked) {
                        this.pickInfo = response.data
                    }
                })
                .catch(() => {
                    if (companyId === this.picked) {
                        this.pickError = `Unable to look up ${companyId}.`
                    }
                })
        },
    },
    methods: {
        // From picking the site until the last site's tree arrived.
        pickLoaded() {
            this.pickLoadMs = Math.round(performance.now() - this.pickStartedAt)
        },
        buildStressTree() {
            const {sites, areas, zones, points} = this.generator
            this.values.stress = {}
            this.stressTree = Object.freeze(Array.from({length: sites}, (unused, s) => ({
                id: `site_${s + 1}`,
                name: `Site ${s + 1}`,
                areas: syntheticAreas({areas, zones, points}),
            })))
        },
        // Click (or input) to the next paint. Vue renders in a microtask, so it is
        // done before the animation frame; the timeout lands after the paint.
        startTiming(action) {
            const start = performance.now()
            requestAnimationFrame(() => setTimeout(() => {
                this.lastTiming = {action, ms: Math.round(performance.now() - start)}
            }, 0))
        },
        // Large selections are cut so the panel stays readable.
        preview(value) {
            const text = JSON.stringify(value)
            return text.length > 2000 ? `${text.slice(0, 2000)}... (${text.length.toLocaleString()} characters)` : text
        },
    },
}
</script>
